import { Capacitor } from '@capacitor/core';
import CloseIcon from '@mui/icons-material/Close';
import { GlobalStyles, IconButton, styled } from '@mui/material';
import { MaterialDesignContent, SnackbarProvider as NSnackbarProvider, SnackbarKey } from 'notistack';
import { MouseEventHandler, useCallback, useRef } from 'react';
import { safeAreaInset } from '../layout/SafeArea';

declare module 'notistack' {
  interface VariantOverrides {
    notification: true;
  }
}

interface Props {
  children: React.ReactElement;
}

const NotificationMaterialDesignContent = styled(MaterialDesignContent)(({ theme }) => ({
  flexWrap: 'nowrap',
  alignItems: 'flex-start',
  '&.notistack-MuiContent-notification': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  [theme.breakpoints.down('sm')]: {
    // hide actions on small screens
    '& > #notistack-snackbar + div': {
      display: 'none',
    },
  },
}));

export const SnackbarProvider: React.FC<Props> = ({ children }) => {
  const snackbarRef = useRef<NSnackbarProvider>(null);

  const onClickDismiss = useCallback(
    (key: SnackbarKey): MouseEventHandler<HTMLButtonElement> =>
      ev => {
        ev.stopPropagation();
        snackbarRef.current?.closeSnackbar(key);
      },
    [],
  );

  const action = useCallback(
    (key: SnackbarKey) => (
      <IconButton color="inherit" onClick={onClickDismiss(key)}>
        <CloseIcon />
      </IconButton>
    ),
    [onClickDismiss],
  );

  return (
    <>
      <GlobalStyles
        styles={
          Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android'
            ? {
                '.notistack-SafeAreaBottom': {
                  bottom: `calc(20px + ${safeAreaInset('bottom')}) !important`,
                },
                '.notistack-SafeAreaTop': {
                  top: `calc(20px + ${safeAreaInset('top')}) !important`,
                },
              }
            : undefined
        }
      />
      <NSnackbarProvider
        ref={snackbarRef}
        maxSnack={3}
        action={action}
        Components={{ notification: NotificationMaterialDesignContent }}
        classes={{
          containerAnchorOriginBottomLeft: 'notistack-SafeAreaBottom',
          containerAnchorOriginBottomCenter: 'notistack-SafeAreaBottom',
          containerAnchorOriginBottomRight: 'notistack-SafeAreaBottom',
          containerAnchorOriginTopLeft: 'notistack-SafeAreaTop',
          containerAnchorOriginTopCenter: 'notistack-SafeAreaTop',
          containerAnchorOriginTopRight: 'notistack-SafeAreaTop',
        }}
      >
        {children}
      </NSnackbarProvider>
    </>
  );
};
